import React, { useEffect, useState, useCallback } from 'react';
import { Play, Pause, Trash2, Plus } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';

const BADGE = { draft: 'bg-[#F3F2F2] text-[#5C5C5C]', running: 'bg-[#E3F3E8] text-[#036B3A]', paused: 'bg-[#FFF4E5] text-[#8C4B02]', completed: 'bg-[#D6EAFB] text-[#0176D3]' };
const INPUT = 'border border-[#D8DDE6] rounded-[4px] px-3 py-2 text-[13px] focus:outline-none focus:border-[#0176D3]';
const err = (e, fallback) => e.response?.data?.error || fallback;
const BLANK = { name: '', numbers: '', maxConcurrent: 2, callsPerMinute: 4 };

// One "number, name" pair per line; the name is optional.
const parseNumbers = (text) => text.split('\n').map((l) => l.trim()).filter(Boolean).map((l) => { const [phone, ...rest] = l.split(','); return { phone: phone.trim(), name: rest.join(',').trim() }; });

export default function VoiceCampaignsPanel() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    try { setRows((await api.get('/voice/campaigns')).data.data || []); } catch (e) { alertService.error('Error', err(e, 'Could not load campaigns')); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      await api.post('/voice/campaigns', { name: form.name, contacts: parseNumbers(form.numbers), maxConcurrent: Number(form.maxConcurrent), callsPerMinute: Number(form.callsPerMinute) });
      setOpen(false); setForm(BLANK); load();
    } catch (e) { alertService.error('Error', err(e, 'Could not create the campaign')); } finally { setBusy(false); }
  };
  const act = async (id, action) => {
    try { await api.post(`/voice/campaigns/${id}/${action}`); load(); } catch (e) { alertService.error('Error', err(e, `Could not ${action} the campaign`)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/voice/campaigns/${id}`); load(); } catch (e) { alertService.error('Error', err(e, 'Could not delete the campaign')); }
  };
  const show = async (id) => { try { setDetail((await api.get(`/voice/campaigns/${id}`)).data.data); } catch (e) { alertService.error('Error', err(e, 'Could not load the campaign')); } };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <p className="text-[12px] text-[#706E6B]">The agent dials each number, retries no-answer, busy and voicemail later (retry rules come from Calling Agent compliance settings), and respects Do-Not-Disturb and calling hours.</p>
        <button onClick={() => setOpen(true)} className="flex items-center gap-1 bg-[#0176D3] hover:bg-[#014486] text-white text-[13px] font-semibold px-3 h-8 rounded whitespace-nowrap"><Plus size={14} /> New campaign</button>
      </div>

      {rows.map((c) => (
        <div key={c.id} className="bg-white border border-[#E5E5E5] rounded-lg p-4 flex flex-wrap items-center gap-3 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
          <button onClick={() => show(c.id)} className="flex-1 min-w-[180px] text-left">
            <div className="flex items-center gap-2"><span className="text-sm font-semibold text-[#2E2E2E]">{c.name}</span><span className={`text-xs px-2 py-0.5 rounded-full ${BADGE[c.status]}`}>{c.status}</span></div>
            <div className="text-xs text-[#706E6B] mt-1">{c.total} numbers · {c.done} done · {c.pending + c.calling} waiting · {c.failed} failed · {c.skipped} skipped</div>
          </button>
          {['draft', 'paused'].includes(c.status) && <button onClick={() => act(c.id, 'start')} className="flex items-center gap-1 text-[13px] font-semibold text-[#2E844A]"><Play size={14} /> Start</button>}
          {c.status === 'running' && <button onClick={() => act(c.id, 'pause')} className="flex items-center gap-1 text-[13px] font-semibold text-[#8C4B02]"><Pause size={14} /> Pause</button>}
          {c.status !== 'running' && <button onClick={() => remove(c.id)} aria-label="Delete campaign" className="text-[#706E6B] hover:text-[#BA0517]"><Trash2 size={15} /></button>}
        </div>
      ))}
      {!rows.length && <div className="text-center py-12 border-2 border-dashed border-[#E5E5E5] rounded-lg text-[#706E6B] text-sm">No campaigns yet</div>}

      {open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md p-5 space-y-3 max-h-[100dvh] overflow-y-auto">
            <h3 className="text-[16px] font-bold">New campaign</h3>
            <input className={`${INPUT} w-full`} placeholder="Campaign name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <textarea className={`${INPUT} w-full resize-none`} rows={6} placeholder={'One per line: number, name\n+919876543210, Asha\n+919812345678'} value={form.numbers} onChange={(e) => setForm({ ...form, numbers: e.target.value })} />
            <div className="grid grid-cols-2 gap-2 text-[12px] text-[#706E6B]">
              <label>Calls at once<input type="number" min="1" max="20" className={`${INPUT} w-full mt-1`} value={form.maxConcurrent} onChange={(e) => setForm({ ...form, maxConcurrent: e.target.value })} /></label>
              <label>Calls started per minute<input type="number" min="1" max="60" className={`${INPUT} w-full mt-1`} value={form.callsPerMinute} onChange={(e) => setForm({ ...form, callsPerMinute: e.target.value })} /></label>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="px-3 h-8 text-[13px] text-[#706E6B]">Cancel</button>
              <button onClick={create} disabled={busy || !form.name.trim() || !form.numbers.trim()} className="bg-[#0176D3] disabled:opacity-50 text-white text-[13px] font-semibold px-4 h-8 rounded">{busy ? 'Creating…' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-lg w-full max-w-lg p-5 max-h-[80dvh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-[16px] font-bold mb-3">{detail.name}</h3>
            <table className="w-full text-xs">
              <thead><tr className="text-left text-[#706E6B]"><th className="py-1">Number</th><th>Status</th><th>Tries</th><th>Last result</th></tr></thead>
              <tbody>{(detail.contacts || []).map((c, i) => <tr key={i} className="border-t border-[#F3F2F2]"><td className="py-1.5 font-mono">{c.phone}{c.name ? ` · ${c.name}` : ''}</td><td>{c.status}</td><td>{c.attempts}</td><td>{c.lastOutcome || '—'}</td></tr>)}</tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
